import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "./config";

export const resolveMediaUrl = (value, fallback = "") => {
  if (!value) return fallback;
  if (typeof value !== "string") return fallback;
  if (/^https?:\/\//i.test(value) || value.startsWith("data:")) return value;
  return value;
};

export const normalizeCategory = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    categori_name_ar: data.categori_name_ar || data.name || "",
    categori_photo: data.categori_photo || data.photoUrl || "",
    sort_order: Number(data.sort_order ?? data.sortOrder ?? 0),
    is_active: data.is_active !== false,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
};

export const normalizeProduct = (docSnap) => {
  const data = docSnap.data() || {};
  const incomingImages = Array.isArray(data.images) ? data.images : [];
  const normalizedImages = incomingImages
    .map((imageItem) => {
      if (!imageItem) return null;
      if (typeof imageItem === "string") {
        return { product_photo: imageItem };
      }
      if (typeof imageItem === "object" && imageItem.product_photo) {
        return { product_photo: imageItem.product_photo };
      }
      if (typeof imageItem === "object" && imageItem.url) {
        return { product_photo: imageItem.url };
      }
      return null;
    })
    .filter(Boolean);

  return {
    id: docSnap.id,
    product_title_ar: data.product_title_ar || data.title || "",
    product_description_ar: data.product_description_ar || data.description || "",
    product_price: Number(data.product_price ?? data.price ?? 0),
    product_code: data.product_code || data.code || docSnap.id,
    categories_id: data.categories_id || data.categoryId || "",
    images: normalizedImages,
    in_discount: Number(data.in_discount ?? 0),
    discount_price: Number(data.discount_price ?? 0),
    is_active: data.is_active !== false,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
};

export const normalizeOrder = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    order_code: data.order_code || data.code || docSnap.id,
    customer_name: data.customer_name || data.customer?.name || "",
    email: data.email || data.customer?.email || "",
    phone: data.phone || data.customer?.phone || "",
    address: data.address || data.customer?.address || "",
    city: data.city || data.customer?.city || "",
    note: data.note || data.customer?.note || "",
    items: Array.isArray(data.items) ? data.items : [],
    total_price: Number(data.total_price ?? data.totalPrice ?? 0),
    status: data.status || "new",
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
};

export const normalizeContact = (docSnap) => {
  const data = docSnap.data() || {};
  return {
    id: docSnap.id,
    name: data.name || "",
    email: data.email || "",
    phone: data.phone || "",
    subject: data.subject || "",
    message: data.message || "",
    status: data.status || "new",
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
};

export const ensureAdminProfile = async (user) => {
  if (!user?.uid) return null;

  const ref = doc(db, "admins", user.uid);
  const snap = await getDoc(ref);

  if (!snap.exists()) {
    await setDoc(
      ref,
      {
        uid: user.uid,
        email: user.email || "",
        displayName: user.displayName || "",
        role: "super_admin",
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      },
      { merge: true }
    );
  } else {
    await setDoc(
      ref,
      {
        email: user.email || snap.data()?.email || "",
        displayName: user.displayName || snap.data()?.displayName || "",
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  const fresh = await getDoc(ref);
  return fresh.exists() ? fresh.data() : null;
};

export const formatDateTime = (value) => {
  if (!value) return "—";

  try {
    const date =
      typeof value?.toDate === "function"
        ? value.toDate()
        : value instanceof Date
        ? value
        : new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("ar-IQ", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch (error) {
    return "—";
  }
};

export const stripHtml = (value = "") => {
  return String(value).replace(/<[^>]*>?/gm, "").trim();
};